import {
  createUserWithEmailAndPassword,
  signOut,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  doc,
  setDoc,
  serverTimestamp,
  Timestamp,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  arrayUnion,
  deleteDoc,
} from "firebase/firestore";
import { auth, db, storage } from "../config/firebase"; // Ensure this path is correct
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import * as Sentry from "@sentry/react";

const signUp = async (email, password) => {
  try {
    // Create user
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password,
    );
    const user = userCredential.user;

    // Store user
    await setDoc(doc(db, "users", user.uid), {
      uid: user.uid,
      email: user.email,
      createdAt: serverTimestamp(), // ✅ Auto timestamp
      roles: ["applicant"], // ✅ Store multiple roles as an array
    });

    return { user };
  } catch (error) {
    Sentry.captureException(error);
    return { user: null, error: error.message }; // ✅ Return error message
  }
};

const logIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password,
    );
    const user = userCredential.user;

    return { user, error: null }; // Return user and null error
  } catch (error) {
    console.error(`❌ Login Error (${error.code}): ${error.message}`);

    // Send error to Sentry for tracking
    Sentry.captureException(error);

    return { user: null, error: error.message }; // Return error message
  }
};

const logOut = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error signing out:", error);
    Sentry.captureException(error);
  }
};

const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    console.log("✅ Password reset email sent!");
    return { success: true };
  } catch (error) {
    console.error("❌ Error sending password reset email:", error.message);
    Sentry.captureException(error);
    return { error: error.message };
  }
};

const saveFirstAndLastName = async (firstName, lastName) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No authenticated user found");

    const userRef = doc(db, "users", user.uid); // Reference to user's document

    await updateDoc(userRef, {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      updatedAt: new Date(), // Optional: track last update time
    });

    console.log("✅ User profile updated successfully!");
    return { success: true };
  } catch (error) {
    console.error("❌ Error updating user profile:", error.message);
    return { success: false, error: error.message };
  }
};

const saveBirthdayCellphoneAndAddress = async (
  birthday,
  cellphone,
  address,
) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No authenticated user found");

    const userRef = doc(db, "users", user.uid); // Reference to user's document

    await updateDoc(userRef, {
      birthday: birthday,
      cellphone: cellphone,
      address: address,
      updatedAt: new Date(), // Optional: track last update time
    });

    console.log("✅ User profile updated successfully!");
    return { success: true };
  } catch (error) {
    console.error("❌ Error updating user profile:", error.message);
    return { success: false, error: error.message };
  }
};

const saveApplicationScreen3 = async (data, profile) => {
  if (!profile?.uid) {
    console.error("User is not authenticated");
    return { success: false, message: "User not authenticated" };
  }
  try {
    const currentYear = new Date().getFullYear(); // Get the current year
    const applicationDeadline = new Date(currentYear, 3, 1); // April 1st of the current year (month is 0-indexed)

    const applicationRef = doc(
      db,
      "Submissions",
      "ScholarshipApplications",
      currentYear.toString(), // Store applications under "ScholarshipApplications/{year}/{userId}"
      profile.uid,
    );

    // Data to save
    const applicationData = {
      firstName: profile?.firstName || data?.firstName,
      lastName: profile?.lastName || data?.lastName,
      birthday: profile?.birthday || data?.birthday,
      cellphone: profile?.cellphone || data?.cellphone,
      address: profile?.address || data?.address,
      email: profile?.email || data?.email,
      uid: profile?.uid || data?.uid,
      updatedAt: serverTimestamp(), // Firestore timestamp
      applicantLevel: data?.applicantLevel,
      previousWinner: data?.previousWinner,
      yearReceived: data?.yearReceived || null,
      alphaMentor: data?.alphaMentor,
      mentorName: data?.mentorName || null,
      alphaFamilyMember: data?.alphaFamilyMember || null,
      familyMemberName: data?.familyMemberName || null,
      familyMemberChapter: data?.familyMemberChapter || null,
      applicationDeadline: Timestamp.fromDate(applicationDeadline), // Firestore timestamp for deadline
    };

    await setDoc(applicationRef, applicationData, { merge: true });

    return { success: true };
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error saving scholarship application:", error);
    return { success: false, message: error.message };
  }
};

const saveApplicationScreen4 = async (data, profile) => {
  if (!profile?.uid) {
    console.error("User is not authenticated");
    return { success: false, message: "User not authenticated" };
  }
  try {
    const currentYear = new Date().getFullYear(); // Get the current year

    const applicationRef = doc(
      db,
      "Submissions",
      "ScholarshipApplications",
      currentYear.toString(), // Store applications under "ScholarshipApplications/{year}/{userId}"
      profile.uid,
    );

    // Data to save
    const applicationData = {
      schoolName: data?.schoolName,
      schoolAddress: data?.schoolAddress,
      gpa: data?.gpa,
      major: data?.major || null,
      minor: data?.minor || null,
      academicClassification: data?.academicClassification || null,
      collegesAccepted: data?.collegesAccepted || null,
      collegesPending: data?.collegesPending || null,
      updatedAt: serverTimestamp(), // Firestore timestamp
    };

    await setDoc(applicationRef, applicationData, { merge: true });

    return { success: true };
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error saving scholarship application:", error);
    return { success: false, message: error.message };
  }
};

const saveApplicationScreen5 = async (data, profile) => {
  if (!profile?.uid) {
    console.error("User is not authenticated");
    return { success: false, message: "User not authenticated" };
  }
  try {
    const currentYear = new Date().getFullYear(); // Get the current year

    const applicationRef = doc(
      db,
      "Submissions",
      "ScholarshipApplications",
      currentYear.toString(), // Store applications under "ScholarshipApplications/{year}/{userId}"
      profile.uid,
    );

    // Data to save
    const applicationData = {
      careerGoals: data?.careerGoals || null,
      involvement: data?.involvement || null,
      updatedAt: serverTimestamp(), // Firestore timestamp
    };

    await setDoc(applicationRef, applicationData, { merge: true });

    return { success: true };
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error saving scholarship application:", error);
    return { success: false, message: error.message };
  }
};

const uploadFile = async (file, name, profile) => {
  if (!file) throw new Error("No file provided");

  const currentYear = new Date().getFullYear(); // Get the current year

  const storageRef = ref(
    storage,
    `Submissions/ScholarshipApplications/${currentYear}/${profile?.uid}/${name}`,
  );

  // Upload the file
  await uploadBytes(storageRef, file);

  const downloadURL = await getDownloadURL(storageRef);
  return downloadURL;
};

const submitApplication = async (urls, profile) => {
  if (!profile?.uid) {
    console.error("User is not authenticated");
    return { success: false, message: "User not authenticated" };
  }

  const currentYear = new Date().getFullYear(); // Get the current year

  try {
    const applicationRef = doc(
      db,
      "Submissions",
      "ScholarshipApplications",
      currentYear.toString(), // Store applications under "ScholarshipApplications/{year}/{userId}"
      profile.uid,
    );

    await setDoc(applicationRef, urls, { merge: true });

    return { success: true };
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error saving scholarship application:", error);
    return { success: false, message: error.message };
  }
};

const getReviewerScores = async (applicant, currentYear) => {
  try {
    // 1. Reference to the reviewers subcollection
    const reviewsRef = collection(
      db,
      "Submissions",
      "ScholarshipApplications",
      currentYear.toString(),
      applicant.uid,
      "reviewers",
    );

    const querySnapshot = await getDocs(reviewsRef);

    // 2. Process each reviewer document
    const reviewersWithScores = [];

    querySnapshot.forEach((doc) => {
      const reviewData = doc.data();

      // Extract all score fields (keys ending with 'Score')
      const scoreKeys = Object.keys(reviewData).filter(
        (key) => key.endsWith("Score") && typeof reviewData[key] === "number",
      );

      // Calculate total score
      const totalScore = scoreKeys.reduce(
        (sum, key) => sum + reviewData[key],
        0,
      );

      // Get display name (firstName or email prefix)
      const displayName =
        reviewData.firstName || reviewData.email?.split("@")[0] || "Reviewer";

      reviewersWithScores.push({
        name: displayName,
        score: totalScore,
        updatedAt: reviewData.updatedAt?.toDate().toLocaleDateString() || "N/A",
        uid: doc.id,
        // Include individual scores if needed
        individualScores: scoreKeys.reduce((obj, key) => {
          obj[key] = reviewData[key];
          return obj;
        }, {}),
      });
    });

    // 3. Sort by most recent first
    reviewersWithScores.sort(
      (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
    );

    return reviewersWithScores;
  } catch (error) {
    console.error("Error fetching reviewer scores:", error);
    Sentry.captureException(error);
    return [];
  }
};

const saveScore = async (
  applicant,
  profile,
  navigate,
  gpaScore,
  collegeLocationScore,
  personalStatementScore,
  firstLetterOfRecScore,
  secondLetterOfRecScore,
  communityServiceScore,
  hasAMentorScore,
  currentMemberScore,
  attendingHBCUScore,
  pastWinnerScore,
) => {
  const currentYear = new Date().getFullYear().toString();

  const reviewDocRef = doc(
    db,
    `Submissions/ScholarshipApplications/${currentYear}/${applicant.uid}/reviewers/${profile.uid}`,
  );

  try {
    await setDoc(
      reviewDocRef,
      {
        gpaScore: gpaScore,
        collegeLocationScore: collegeLocationScore || 0,
        personalStatementScore: personalStatementScore || 0,
        firstLetterOfRecScore: firstLetterOfRecScore || 0,
        secondLetterOfRecScore: secondLetterOfRecScore || 0,
        communityServiceScore: communityServiceScore,
        hasAMentorScore: hasAMentorScore || 0,
        currentMemberScore: currentMemberScore || 0,
        attendingHBCUScore: attendingHBCUScore || 0,
        pastWinnerScore: pastWinnerScore || 0,
        firstName: profile?.firstName || null,
        email: profile?.email,
        uid: profile?.uid,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    navigate("/submission-dashboard", { replace: true }); // Navigate programmatically
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error submitting score: ", error);
    alert("Failed to submit score. Please try again.");
  }
};

const handleGiveReviewAccess = async (email, role, setMessage) => {
  try {
    // Reference to the users collection
    const usersRef = collection(db, "users");

    // Query Firestore to find the user by email
    const q = query(usersRef, where("email", "==", email));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      setMessage("User not found with email: " + email);
      return;
    }

    // Get the user's document ID
    const userDoc = querySnapshot.docs[0];
    const userDocRef = userDoc.ref;

    // If granting admin access, also grant reviewer access
    if (role === "giveReviewAccess") {
      await updateDoc(userDocRef, {
        roles: arrayUnion("reviewer", "giveReviewAccess"),
      });
    } else {
      // Just add the single role
      await updateDoc(userDocRef, {
        roles: arrayUnion(role),
      });
    }

    const roleDisplayName = role === "giveReviewAccess" ? "Admin" : "Reviewer";
    setMessage(`${roleDisplayName} access granted to ${email}`);
  } catch (error) {
    console.error("Error granting access:", error);
    setMessage("Error granting access: " + error.message);
    Sentry.captureException(error);
  }
};

const uploadDocumentForApplicant = async (file, fieldName, applicantUid) => {
  if (!file) throw new Error("No file provided");

  const currentYear = new Date().getFullYear();

  // Upload to the applicant's storage location
  const storageRef = ref(
    storage,
    `Submissions/ScholarshipApplications/${currentYear}/${applicantUid}/${fieldName}`,
  );

  // Upload the file
  await uploadBytes(storageRef, file);

  // Get download URL
  const downloadURL = await getDownloadURL(storageRef);

  // Update the applicant's Firestore document with the new URL
  const applicationRef = doc(
    db,
    "Submissions",
    "ScholarshipApplications",
    currentYear.toString(),
    applicantUid,
  );

  await updateDoc(applicationRef, {
    [fieldName]: downloadURL,
    updatedAt: serverTimestamp(),
  });

  return downloadURL;
};

const archiveApplication = async (applicant) => {
  if (!applicant?.uid) {
    console.error("No applicant uid provided");
    return { success: false, message: "No applicant uid provided" };
  }

  const currentYear = new Date().getFullYear();

  try {
    // Strip runtime-added fields before archiving
    const { id, reviewers, totalScore, ...applicantData } = applicant;

    // Write to archive collection
    const archiveRef = doc(
      db,
      "Submissions",
      "ArchivedApplications",
      currentYear.toString(),
      applicant.uid,
    );

    await setDoc(archiveRef, {
      ...applicantData,
      archivedAt: serverTimestamp(),
      archivedYear: currentYear,
    });

    // Mark original as archived
    const originalRef = doc(
      db,
      "Submissions",
      "ScholarshipApplications",
      currentYear.toString(),
      applicant.uid,
    );

    await updateDoc(originalRef, {
      archived: true,
      archivedAt: serverTimestamp(),
    });

    return { success: true };
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error archiving application:", error);
    return { success: false, message: error.message };
  }
};

const getArchivedApplications = async (years) => {
  try {
    const results = await Promise.all(
      years.map(async (year) => {
        const baseRef = collection(
          db,
          "Submissions",
          "ArchivedApplications",
          year.toString(),
        );
        const snapshot = await getDocs(baseRef);
        const applicants = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        return { year, applicants };
      }),
    );
    return results;
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error fetching archived applications:", error);
    return [];
  }
};

const unarchiveApplication = async (applicant, year) => {
  if (!applicant?.uid) {
    console.error("No applicant uid provided");
    return { success: false, message: "No applicant uid provided" };
  }

  if (!year) {
    console.error("No year provided");
    return { success: false, message: "No year provided" };
  }

  try {
    // Remove the archived flag from the original
    const originalRef = doc(
      db,
      "Submissions",
      "ScholarshipApplications",
      year.toString(),
      applicant.uid,
    );

    await updateDoc(originalRef, {
      archived: false,
      unarchivedAt: serverTimestamp(),
    });

    // Delete the archive document
    const archiveRef = doc(
      db,
      "Submissions",
      "ArchivedApplications",
      year.toString(),
      applicant.uid,
    );

    await deleteDoc(archiveRef);

    return { success: true };
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error unarchiving application:", error);
    return { success: false, message: error.message };
  }
};

export {
  signUp,
  logOut,
  logIn,
  resetPassword,
  saveFirstAndLastName,
  saveBirthdayCellphoneAndAddress,
  saveApplicationScreen3,
  saveApplicationScreen4,
  saveApplicationScreen5,
  uploadFile,
  submitApplication,
  getReviewerScores,
  saveScore,
  handleGiveReviewAccess,
  uploadDocumentForApplicant,
  archiveApplication,
  getArchivedApplications,
  unarchiveApplication,
};
